import { Box, Card, Typography, Button, useTheme } from "@mui/material";
import {
    FaArrowRight,
    FaBoxOpen,
    FaClock,
    FaIndustry,
    FaMagnifyingGlass,
    FaTriangleExclamation,
    FaUserPlus,
    FaUsers,
    FaWallet,
} from "react-icons/fa6";
import { LineChart, BarChart, PieChart } from "@mui/x-charts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import PageLayout from "../../components/PageLayout";
import TableLoading from "../../components/TableLoading";
import {
    formatCurrency,
    formatDateShort,
    formatDays,
    formatInteger,
    formatPercent,
} from "../../utils/functions";
import { currentDate, currentMonthDayOne } from "../../utils/data_variables";
import { getDashboard } from "../../helpers/http_dashboard_requests";
import OverdueStamp from "../../components/common/OverdueStamp";
import { late, lateWash } from "../../components/common/tableStyles";

const gold = "#C8A459";

const STATUS_LABELS = {
    recebido: "Recebido",
    aprovado: "Aprovado",
    em_producao: "Em produção",
    pronto: "Pronto",
    entregue: "Entregue",
};

const datePickerSlots = {
    textField: {
        size: "small",
        sx: { width: { xs: "100%", sm: 168 } },
    },
};

function cardShell(span, clickable, tone) {
    const isLate = tone === "late";
    return {
        p: 3,
        minWidth: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        color: "inherit",
        boxShadow: isLate ? `inset 3px 0 0 ${late}` : "none",
        border: "1px solid",
        borderColor: isLate ? late : "divider",
        bgcolor: isLate ? lateWash : undefined,
        gridColumn: {
            xs: "1 / -1",
            sm: `span ${Math.min(span, 2)}`,
            lg: `span ${span}`,
        },
        ...(clickable && {
            cursor: "pointer",
            transition: "border-color .2s ease, background-color .2s ease",
            "&:hover": {
                borderColor: gold,
                bgcolor: "rgba(200,164,89,0.06)",
            },
            "&:focus-visible": {
                outline: `2px solid ${gold}`,
                outlineOffset: 2,
            },
        }),
    };
}

function StatCard({ label, value, hint, accent, icon: Icon, to, span = 1 }) {
    return (
        <Card
            elevation={0}
            component={to ? Link : "div"}
            to={to}
            sx={cardShell(span, Boolean(to))}
        >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            textAlign: "left",
                            pr: 1,
                        }}
                    >
                        {label}
                    </Typography>
                    {Icon ? (
                        <Box sx={styles.iconWrap}>
                            <Icon />
                        </Box>
                    ) : null}
                </Box>
                <Typography
                    variant="h4"
                    sx={{ color: accent || "text.primary", textAlign: "left", fontSize: { xs: "1.5rem", md: "1.75rem" } }}
                >
                    {value}
                </Typography>
                {hint ? (
                    <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", textAlign: "left" }}>
                        {hint}
                    </Typography>
                ) : null}
                {to ? (
                    <Typography sx={styles.cardLink}>
                        Ver área <FaArrowRight size={11} />
                    </Typography>
                ) : null}
        </Card>
    );
}

function PanelCard({ title, icon: Icon, to, children, span = 2, tone }) {
    const isLate = tone === "late";
    return (
        <Card elevation={0} sx={cardShell(span, false, tone)}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        {Icon ? (
                            <Box sx={{ ...styles.iconWrap, ...(isLate ? styles.lateIconWrap : null) }}>
                                <Icon />
                            </Box>
                        ) : null}
                        <Typography variant="h6" sx={{ textAlign: "left", color: isLate ? late : "inherit" }}>
                            {title}
                        </Typography>
                    </Box>
                    {to ? (
                        <Button
                            component={Link}
                            to={to}
                            size="small"
                            endIcon={<FaArrowRight />}
                            sx={{ color: isLate ? late : gold, flexShrink: 0 }}
                        >
                            Abrir
                        </Button>
                    ) : null}
                </Box>
                {children}
        </Card>
    );
}

export default function Dashboard() {
    const theme = useTheme();

    const [startDate, setStartDate] = useState(currentMonthDayOne);
    const [endDate, setEndDate] = useState(currentDate);
    const [appliedStart, setAppliedStart] = useState(currentMonthDayOne);
    const [appliedEnd, setAppliedEnd] = useState(currentDate);

    const requestParams = {
        startDate: appliedStart.format("YYYY-MM-DD"),
        endDate: appliedEnd.format("YYYY-MM-DD"),
    };

    const { data, isFetching } = useQuery({
        queryKey: ["getDashboard", requestParams],
        queryFn: () => getDashboard(requestParams),
        staleTime: 1000 * 10,
    });

    const PeriodPicker = () => (
        <Box sx={styles.filters}>
            <DatePicker
                label="De"
                value={startDate}
                maxDate={endDate}
                onChange={setStartDate}
                format="DD/MM/YYYY"
                slotProps={datePickerSlots}
            />
            <DatePicker
                label="Até"
                value={endDate}
                minDate={startDate}
                onChange={setEndDate}
                format="DD/MM/YYYY"
                slotProps={datePickerSlots}
            />
            <Button
                variant="contained"
                color="secondary"
                startIcon={<FaMagnifyingGlass />}
                sx={styles.searchButton}
                onClick={() => {
                    setAppliedStart(startDate);
                    setAppliedEnd(endDate);
                }}
            >
                Buscar
            </Button>
        </Box>
    );

    if (isFetching || !data?.data?.data) {
        return (
            <PageLayout pageTitle="Painel" customComponent={PeriodPicker}>
                <TableLoading />
            </PageLayout>
        );
    }

    const report = data.data.data;
    const variation = formatPercent(report.revenue?.variation_pct);
    const timeline = report.revenue_timeline ?? [];
    const byCategory = report.revenue_by_category ?? [];
    const byMetal = (report.revenue_by_metal ?? []).filter((item) => item.total > 0);
    const byStep = report.production_by_step ?? [];
    const status = report.status_distribution ?? {};
    const overdue = report.overdue ?? { count: 0, orders: [] };
    const topCustomers = report.top_customers ?? [];
    const customers = report.customers ?? {};

    return (
        <PageLayout pageTitle="Painel" customComponent={PeriodPicker}>
            <Box sx={styles.grid}>
                <StatCard
                    label="Faturamento"
                    value={formatCurrency(report.revenue?.current ?? 0)}
                    accent={gold}
                    icon={FaWallet}
                    to="/financeiro"
                    hint={variation ? `${variation} vs período anterior` : "Sem base anterior para comparar"}
                />
                <StatCard
                    label="Ticket médio"
                    value={formatCurrency(report.avg_ticket ?? 0)}
                    icon={FaBoxOpen}
                    to="/pedidos"
                    hint={`${formatInteger(report.orders_count ?? 0)} pedidos no período`}
                />
                <StatCard
                    label="Prazo médio"
                    value={formatDays(report.avg_lead_time_days ?? 0)}
                    icon={FaClock}
                    to="/pedidos"
                    hint="Do pedido à entrega prevista"
                />
                <StatCard
                    label="Novos clientes"
                    value={formatInteger(report.new_customers ?? 0)}
                    icon={FaUserPlus}
                    to="/clientes"
                    hint={`${formatInteger(customers.active_in_period ?? 0)} ativos de ${formatInteger(customers.total ?? 0)}`}
                />

                <PanelCard
                    title={`${formatInteger(overdue.count)} pedido${overdue.count === 1 ? "" : "s"} atrasado${overdue.count === 1 ? "" : "s"}`}
                    icon={FaTriangleExclamation}
                    to="/pedidos?overdue=1"
                    tone={overdue.count > 0 ? "late" : undefined}
                >
                    {overdue.orders.length > 0 ? (
                        overdue.orders.slice(0, 5).map((order) => (
                            <Box
                                key={order.id}
                                component={Link}
                                to={`/pedido-detalhes/${order.id}`}
                                sx={styles.lateRowLink}
                            >
                                <Typography variant="body2" sx={{ textAlign: "left", color: late, fontWeight: 600 }}>
                                    #{order.id} {order.customer}
                                </Typography>
                                <OverdueStamp days={order.overdue_days} />
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left" }}>
                            Nenhum pedido atrasado.
                        </Typography>
                    )}
                </PanelCard>

                <StatCard
                    label="Prontos aguardando"
                    value={formatInteger(report.ready_awaiting_delivery ?? 0)}
                    icon={FaIndustry}
                    to="/producao/pronto"
                    hint="Pedidos prontos ainda não entregues"
                />
                <StatCard
                    label="Parceiros com catálogo"
                    value={formatInteger(customers.with_catalog ?? 0)}
                    icon={FaUsers}
                    to="/clientes"
                    hint={`De ${formatInteger(customers.total ?? 0)} clientes ativos`}
                />

                <PanelCard title="Faturamento no período" icon={FaWallet} to="/financeiro" span={2}>
                    {timeline.length > 0 ? (
                        <Box sx={styles.chartBox}>
                            <LineChart
                                xAxis={[{
                                    data: timeline.map((item) => item.date),
                                    scaleType: "point",
                                    valueFormatter: (value) => formatDateShort(value),
                                }]}
                                yAxis={[{
                                    valueFormatter: (value) => formatCurrency(value),
                                }]}
                                series={[{
                                    data: timeline.map((item) => item.total),
                                    color: gold,
                                    area: true,
                                    valueFormatter: (value) => formatCurrency(value),
                                }]}
                                height={240}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left" }}>
                            Sem vendas no período.
                        </Typography>
                    )}
                </PanelCard>

                <PanelCard title="Faturamento por metal" icon={FaWallet} to="/financeiro" span={2}>
                    {byMetal.length > 0 ? (
                        <Box sx={styles.chartBox}>
                            <PieChart
                                series={[{
                                    data: byMetal.map((item, index) => ({
                                        id: index,
                                        value: item.total,
                                        label: item.label,
                                    })),
                                    valueFormatter: (item) => formatCurrency(item.value),
                                }]}
                                height={240}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left" }}>
                            Sem dados.
                        </Typography>
                    )}
                </PanelCard>

                <PanelCard title="Faturamento por categoria" icon={FaBoxOpen} to="/produtos">
                    {byCategory.length > 0 ? (
                        <Box sx={styles.chartBox}>
                            <BarChart
                                xAxis={[{ data: byCategory.map((item) => item.name), scaleType: "band" }]}
                                yAxis={[{ valueFormatter: (value) => formatCurrency(value) }]}
                                series={[{
                                    data: byCategory.map((item) => item.total),
                                    color: gold,
                                    valueFormatter: (value) => formatCurrency(value),
                                }]}
                                height={240}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left" }}>
                            Sem dados.
                        </Typography>
                    )}
                </PanelCard>

                <PanelCard title="Pedidos por etapa agora" icon={FaIndustry} to="/producao">
                    {byStep.length > 0 ? (
                        <Box sx={styles.chartBox}>
                            <BarChart
                                xAxis={[{ data: byStep.map((item) => item.name), scaleType: "band" }]}
                                yAxis={[{ valueFormatter: (value) => formatInteger(value) }]}
                                series={[{
                                    data: byStep.map((item) => item.count),
                                    color: theme.palette.primary.main,
                                    valueFormatter: (value) => formatInteger(value),
                                }]}
                                height={240}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left" }}>
                            Nada em produção agora.
                        </Typography>
                    )}
                </PanelCard>

                <PanelCard title="Pedidos do período por status" icon={FaBoxOpen} to="/pedidos">
                    {Object.keys(STATUS_LABELS).map((key) => (
                        <Box key={key} sx={styles.statusRow}>
                            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left" }}>
                                {STATUS_LABELS[key]}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatInteger(status[key] ?? 0)}
                            </Typography>
                        </Box>
                    ))}
                </PanelCard>

                <PanelCard title="Top clientes" icon={FaUsers} to="/clientes">
                    {topCustomers.length > 0 ? (
                        topCustomers.map((customer, index) => (
                            <Box
                                key={customer.id || index}
                                component={customer.id ? Link : "div"}
                                to={customer.id ? `/cliente-detalhes/${customer.id}` : undefined}
                                sx={customer.id ? styles.rowLink : styles.statusRow}
                            >
                                <Typography variant="body2" sx={{ textAlign: "left" }}>
                                    {customer.name}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(customer.total)}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left" }}>
                            Sem vendas no período.
                        </Typography>
                    )}
                </PanelCard>
            </Box>
        </PageLayout>
    );
}

const styles = {
    grid: {
        display: "grid",
        gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
        },
        gap: 3,
        alignItems: "start",
        pb: 4,
    },
    chartBox: {
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
    },
    filters: {
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: "wrap",
        alignItems: { xs: "stretch", sm: "flex-end" },
        gap: 1.5,
        flexShrink: 0,
        width: { xs: "100%", lg: "auto" },
    },
    searchButton: {
        minHeight: 40,
        px: 2.5,
        boxShadow: "none",
        whiteSpace: "nowrap",
    },
    iconWrap: {
        width: 40,
        height: 40,
        borderRadius: 1,
        bgcolor: "rgba(200,164,89,0.14)",
        color: gold,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        flexShrink: 0,
    },
    cardLink: {
        mt: "auto",
        pt: 2,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: 13,
        fontWeight: 600,
        color: gold,
    },
    lateIconWrap: {
        bgcolor: "rgba(143,29,29,0.1)",
        color: late,
    },
    lateRowLink: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        py: 1.25,
        textDecoration: "none",
        color: "inherit",
        borderBottom: "1px solid",
        borderColor: "rgba(143,29,29,0.16)",
        "&:last-of-type": {
            borderBottom: "none",
        },
        "&:hover": {
            color: late,
        },
    },
    rowLink: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        py: 1.25,
        textDecoration: "none",
        color: "inherit",
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-of-type": {
            borderBottom: "none",
        },
        "&:hover": {
            color: gold,
        },
    },
    statusRow: {
        display: "flex",
        justifyContent: "space-between",
        py: 1.25,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-of-type": {
            borderBottom: "none",
        },
    },
};
